/**
 * Agentic Payment Transaction History - Shared Utilities
 *
 * Pure functions for data formatting, validation, and transformation.
 */

import { HASHSCAN_BASE_URL } from "./constants";

// ============================================================================
// Date & Time Formatting
// ============================================================================

/**
 * Format a date string to locale string format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date or "-" if invalid
 */
export const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
};

// ============================================================================
// Number Formatting
// ============================================================================

/**
 * Format a numeric amount with appropriate decimal places
 * @param {string|number} amount - The amount to format
 * @returns {string} Formatted amount string
 */
export const formatAmount = (amount) => {
    if (!amount) return "0";
    return parseFloat(amount).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6,
    });
};

// ============================================================================
// URL Builders
// ============================================================================

/**
 * Generate HashScan URL for a transaction
 * @param {string} transactionId - Hedera transaction ID
 * @param {string} network - Network name (mainnet/testnet)
 * @returns {string} Full HashScan URL
 */
export const getHashscanUrl = (transactionId, network) => {
    return `${HASHSCAN_BASE_URL}/${network}/transaction/${transactionId}`;
};

// ============================================================================
// Text Truncation
// ============================================================================

/**
 * Truncate text with ellipsis if exceeds max length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text with ellipsis or original
 */
export const truncateText = (text, maxLength = 20) => {
    if (!text || text.length <= maxLength) return text || "-";
    return `${text.substring(0, maxLength)}...`;
};

// ============================================================================
// Pagination Helpers
// ============================================================================

/**
 * Calculate pagination display range
 * @param {number} currentPage - Current page number
 * @param {number} totalPages - Total number of pages
 * @param {number} showPages - Number of page buttons to show
 * @returns {number[]} Array of page numbers to display
 */
export const calculatePaginationRange = (
    currentPage,
    totalPages,
    showPages = 5,
) => {
    const pages = [];
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    let endPage = Math.min(totalPages, startPage + showPages - 1);

    if (endPage - startPage + 1 < showPages) {
        startPage = Math.max(1, endPage - showPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    return pages;
};

// ============================================================================
// Badge Style Helpers
// ============================================================================

/**
 * Get badge color for transaction category
 * @param {string} category - Transaction category
 * @returns {Object} Style object with background and color
 */
export const getCategoryBadgeStyle = (category) => {
    const styles = {
        Deposit: { background: "#d4edda", color: "#155724" },
        Withdrawal: { background: "#f8d7da", color: "#721c24" },
        "Internal Transfer": { background: "#e8daef", color: "#6c3483" },
        "Token Swap": { background: "#cce5ff", color: "#004085" },
        "Currency Swap": { background: "#fff3cd", color: "#856404" },
        Airdrop: { background: "#d1ecf1", color: "#0c5460" },
    };
    return styles[category] || { background: "#e2e3e5", color: "#383d41" };
};

/**
 * Get badge color for transaction direction
 * @param {string} direction - Transaction direction (incoming/outgoing)
 * @returns {Object} Style object with background and color
 */
export const getDirectionBadgeStyle = (direction) => {
    if (direction === "incoming") {
        return { background: "#d4edda", color: "#155724" };
    }
    if (direction === "outgoing") {
        return { background: "#f8d7da", color: "#721c24" };
    }
    return { background: "#e2e3e5", color: "#383d41" };
};

/**
 * Get badge color for transaction status
 * @param {string} status - Transaction status
 * @returns {Object} Style object with background and color
 */
export const getStatusBadgeStyle = (status) => {
    const styles = {
        success: { background: "#d4edda", color: "#155724" },
        pending: { background: "#fff3cd", color: "#856404" },
        failed: { background: "#f8d7da", color: "#721c24" },
    };
    return (
        styles[status?.toLowerCase()] || { background: "#e2e3e5", color: "#383d41" }
    );
};
